import { useMemo, useState } from 'react';
import type { SessionMeasurementRow } from '../api/sessions';

export interface SessionTimelineChartProps {
  rows: SessionMeasurementRow[];
  cursorIdx: number;
  onCursorChange?: (idx: number) => void;
}

// RSRP quality bands (dBm). Industry standard.
const BANDS = [
  { from: -80,  to: -60,  label: 'Excellent', color: '#22c55e22' },
  { from: -90,  to: -80,  label: 'Good',      color: '#84cc1622' },
  { from: -100, to: -90,  label: 'Fair',      color: '#facc1522' },
  { from: -110, to: -100, label: 'Poor',      color: '#f9731622' },
  { from: -130, to: -110, label: 'Very poor', color: '#ef444422' },
];

function fmtDur(ms: number): string {
  const s = Math.floor(ms / 1000);
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${m}:${String(r).padStart(2, '0')}`;
}

function rsrpLabel(v: number): string {
  if (v >= -80) return 'Excellent';
  if (v >= -90) return 'Good';
  if (v >= -100) return 'Fair';
  if (v >= -110) return 'Poor';
  return 'Very poor';
}

const W = 900, H = 160, PAD_L = 50, PAD_R = 50, PAD_T = 18, PAD_B = 24;

export default function SessionTimelineChart({ rows, cursorIdx, onCursorChange }: SessionTimelineChartProps) {
  const points = useMemo(() => rows
    .filter((r) => r.ts)
    .map((r) => ({ t: new Date(r.ts as string).getTime(), rsrp: r.rsrp, mos: r.mos })),
    [rows]);

  const [hover, setHover] = useState<number | null>(null);

  if (points.length < 2) {
    return <div className="text-slate-500 text-sm p-3">No timeline data.</div>;
  }

  const t0 = points[0].t;
  const tN = points[points.length - 1].t;
  const dur = Math.max(1, tN - t0);
  const plotW = W - PAD_L - PAD_R;
  const plotH = H - PAD_T - PAD_B;

  const xFor = (t: number) => PAD_L + ((t - t0) / dur) * plotW;
  const yRsrp = (v: number) => PAD_T + plotH * (1 - (Math.max(-130, Math.min(-60, v)) + 130) / 70);
  const yMos  = (v: number) => PAD_T + plotH * (1 - (Math.max(1, Math.min(5, v)) - 1) / 4);

  const rsrpRows = points.filter((p) => p.rsrp != null);
  const mosRows  = points.filter((p) => p.mos  != null);

  const dRsrp = rsrpRows
    .map((p, i) => `${i === 0 ? 'M' : 'L'} ${xFor(p.t).toFixed(1)} ${yRsrp(p.rsrp as number).toFixed(1)}`)
    .join(' ');

  // X-axis time ticks (~6 even ticks)
  const ticks = 6;
  const tickStep = dur / ticks;

  const safeIdx = Math.max(0, Math.min(Number.isFinite(cursorIdx) ? cursorIdx : 0, points.length - 1));
  const cur = points[safeIdx];

  const hoverPoint = hover != null
    ? points.reduce((closest, p) => Math.abs(p.t - hover) < Math.abs(closest.t - hover) ? p : closest, points[0])
    : null;

  const handlePointer = (e: React.PointerEvent<SVGSVGElement>) => {
    const svg = e.currentTarget;
    const rect = svg.getBoundingClientRect();
    const xLocal = ((e.clientX - rect.left) / rect.width) * W;
    const tHovered = t0 + Math.max(0, Math.min(1, (xLocal - PAD_L) / plotW)) * dur;
    setHover(tHovered);
    if (onCursorChange && e.buttons === 1) {
      let lo = 0, hi = points.length - 1;
      while (lo < hi) {
        const mid = (lo + hi) >> 1;
        if (points[mid].t < tHovered) lo = mid + 1; else hi = mid;
      }
      if (lo > 0 && Math.abs(points[lo - 1].t - tHovered) < Math.abs(points[lo].t - tHovered)) lo--;
      onCursorChange(lo);
    }
  };

  const curX = xFor(cur.t);
  const hoverX = hoverPoint ? xFor(hoverPoint.t) : null;

  return (
    <div style={{ background: '#0b1220', borderRadius: 10, padding: '8px 12px' }}>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 4 }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: '#f3f4f6', letterSpacing: 0.5 }}>
          Signal over time
        </div>
        <div style={{ fontSize: 11, color: '#9ca3af', display: 'flex', gap: 12 }}>
          <span><span style={{ display: 'inline-block', width: 10, height: 2, background: '#22c55e', verticalAlign: 'middle', marginRight: 4 }} />RSRP (dBm)</span>
          {mosRows.length > 0 && (
            <span><span style={{ display: 'inline-block', width: 10, height: 2, background: '#a855f7', verticalAlign: 'middle', marginRight: 4 }} />MOS (1–5)</span>
          )}
          <span style={{ color: '#6b7280' }}>{fmtDur(dur)} total</span>
        </div>
      </div>

      <svg
        viewBox={`0 0 ${W} ${H}`}
        preserveAspectRatio="none"
        style={{ width: '100%', height: 140, cursor: 'crosshair', userSelect: 'none' }}
        onPointerMove={handlePointer}
        onPointerLeave={() => setHover(null)}
        onPointerDown={handlePointer}
      >
        {/* Quality bands shading */}
        {BANDS.map((b) => (
          <rect
            key={b.label}
            x={PAD_L}
            y={yRsrp(b.to)}
            width={plotW}
            height={Math.max(0, yRsrp(b.from) - yRsrp(b.to))}
            fill={b.color}
          />
        ))}

        {/* horizontal grid + Y labels (RSRP) */}
        {[-130, -110, -100, -90, -80, -60].map((v) => (
          <g key={v}>
            <line x1={PAD_L} y1={yRsrp(v)} x2={W - PAD_R} y2={yRsrp(v)} stroke="#1f2937" strokeWidth={1} />
            <text x={PAD_L - 8} y={yRsrp(v) + 4} fontSize={10} fill="#9ca3af" textAnchor="end">{v}</text>
          </g>
        ))}

        {/* MOS Y labels right */}
        {mosRows.length > 0 && [1, 3, 5].map((v) => (
          <text key={v} x={W - PAD_R + 8} y={yMos(v) + 4} fontSize={10} fill="#a855f7" textAnchor="start">{v}</text>
        ))}

        {/* X-axis time ticks */}
        {Array.from({ length: ticks + 1 }).map((_, i) => {
          const t = t0 + i * tickStep;
          const x = xFor(t);
          return (
            <g key={i}>
              <line x1={x} y1={H - PAD_B} x2={x} y2={H - PAD_B + 4} stroke="#4b5563" />
              <text x={x} y={H - PAD_B + 16} fontSize={10} fill="#9ca3af" textAnchor="middle">
                {fmtDur(t - t0)}
              </text>
            </g>
          );
        })}

        {/* Axis title */}
        <text x={PAD_L} y={H - 4} fontSize={10} fill="#6b7280">elapsed</text>
        <text x={4} y={PAD_T - 8} fontSize={10} fill="#22c55e">dBm</text>
        {mosRows.length > 0 && (
          <text x={W - PAD_R + 4} y={PAD_T - 8} fontSize={10} fill="#a855f7">MOS</text>
        )}

        {/* RSRP line */}
        {dRsrp && <path d={dRsrp} fill="none" stroke="#22c55e" strokeWidth={2} strokeLinejoin="round" strokeLinecap="round" />}

        {/* MOS dots (sparse measureNow events) */}
        {mosRows.map((p, i) => (
          <g key={i}>
            <line x1={xFor(p.t)} y1={PAD_T} x2={xFor(p.t)} y2={H - PAD_B} stroke="#a855f7" strokeOpacity={0.08} />
            <circle cx={xFor(p.t)} cy={yMos(p.mos as number)} r={3} fill="#a855f7" stroke="#0b1220" strokeWidth={1.5} />
          </g>
        ))}

        {/* Cursor line (driven by map slider) */}
        <line x1={curX} y1={PAD_T} x2={curX} y2={H - PAD_B} stroke="#3b82f6" strokeWidth={1.5} strokeDasharray="3 3" />
        {cur.rsrp != null && (
          <circle cx={curX} cy={yRsrp(cur.rsrp)} r={4} fill="#3b82f6" stroke="#0b1220" strokeWidth={1.5} />
        )}

        {/* Hover line + tooltip */}
        {hoverX != null && hoverPoint && (
          <>
            <line x1={hoverX} y1={PAD_T} x2={hoverX} y2={H - PAD_B} stroke="#9ca3af" strokeWidth={1} strokeDasharray="2 2" />
            {hoverPoint.rsrp != null && (
              <circle cx={hoverX} cy={yRsrp(hoverPoint.rsrp)} r={3.5} fill="#22c55e" stroke="#0b1220" strokeWidth={1.5} />
            )}
            {hoverPoint.mos != null && (
              <circle cx={hoverX} cy={yMos(hoverPoint.mos)} r={3.5} fill="#a855f7" stroke="#0b1220" strokeWidth={1.5} />
            )}
            {/* Tooltip box */}
            {(() => {
              const tipW = 160;
              const tipH = hoverPoint.mos != null ? 64 : 48;
              const left = hoverX + 8 + tipW > W - PAD_R ? hoverX - tipW - 8 : hoverX + 8;
              const top = PAD_T + 4;
              return (
                <g>
                  <rect x={left} y={top} width={tipW} height={tipH} rx={6} fill="#111827" stroke="#1f2937" />
                  <text x={left + 8} y={top + 14} fontSize={10} fill="#9ca3af">at {fmtDur(hoverPoint.t - t0)}</text>
                  <text x={left + 8} y={top + 30} fontSize={11} fill="#22c55e" fontWeight={700}>
                    RSRP: {hoverPoint.rsrp != null ? `${hoverPoint.rsrp} dBm` : '—'}
                  </text>
                  {hoverPoint.rsrp != null && (
                    <text x={left + 8} y={top + 44} fontSize={9} fill="#9ca3af">{rsrpLabel(hoverPoint.rsrp)}</text>
                  )}
                  {hoverPoint.mos != null && (
                    <text x={left + 8} y={top + 58} fontSize={11} fill="#a855f7" fontWeight={700}>
                      MOS: {hoverPoint.mos.toFixed(2)}
                    </text>
                  )}
                </g>
              );
            })()}
          </>
        )}
      </svg>
    </div>
  );
}
